import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import { absUrl, clock } from "@/lib/voiceSongsApi";

/** Two versions of the same song on one transport — switch A/B and keep your place. */
export default function ABPlayer({ a, b, testId }) {
  const refs = { a: useRef(null), b: useRef(null) };
  const [which, setWhich] = useState("b");
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const el = (k) => refs[k].current;
  useEffect(() => () => { el("a")?.pause(); el("b")?.pause(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => {
    const cur = el(which);
    if (playing) { cur.pause(); setPlaying(false); } else { cur.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
  };
  const pick = (k) => {
    if (k === which) return;
    const from = el(which);
    const to = el(k);
    to.currentTime = Math.min(from.currentTime, to.duration || from.currentTime);
    if (playing) { from.pause(); to.play().catch(() => setPlaying(false)); }
    setWhich(k);
  };
  const seek = (v) => { el(which).currentTime = v; setT(v); };
  const opts = { a, b };
  return (
    <div data-testid={testId} className="mt-3 rounded-2xl border border-white/[0.08] bg-black/30 p-3">
      {["a", "b"].map((k) => (
        <audio key={k} ref={refs[k]} src={absUrl(opts[k].src)} preload="metadata"
          onTimeUpdate={(e) => k === which && setT(e.currentTarget.currentTime)} onLoadedMetadata={(e) => k === which && setDur(e.currentTarget.duration)}
          onEnded={() => k === which && setPlaying(false)} className="hidden" />
      ))}
      <div className="flex items-center gap-3">
        <button type="button" data-testid={`${testId}-play`} onClick={toggle} aria-label={playing ? "Pause" : "Play"} className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full bg-white text-black transition-transform hover:scale-105">
          {playing ? <Pause className="h-4 w-4" /> : <Play className="ml-0.5 h-4 w-4" />}
        </button>
        <div className="flex rounded-full border border-white/10 bg-black/40 p-1">
          {["a", "b"].map((k) => (
            <button key={k} type="button" data-testid={`${testId}-pick-${k}`} aria-pressed={which === k} onClick={() => pick(k)}
              className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors ${which === k ? "bg-cyan-300 text-black" : "text-white/60 hover:text-white"}`}>{opts[k].label}</button>
          ))}
        </div>
        <input type="range" aria-label="Position" min={0} max={dur || 0} step={0.1} value={Math.min(t, dur || 0)} onChange={(e) => seek(Number(e.target.value))} className="h-1.5 min-w-0 flex-1 accent-cyan-300" />
        <span data-testid={`${testId}-time`} className="font-mono text-[11px] tabular-nums text-white/60">{clock(t)} / {clock(dur)}</span>
      </div>
    </div>
  );
}
